// TextBoxConfigModal.jsx - Configuration modal for text box elements
import React, { useState, useEffect } from 'react';
import { X, Type, Tag, Palette, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import SearchableTagSelect from './SearchableTagSelect';

const FONT_FAMILIES = [
  { value: 'Inter, system-ui, sans-serif', label: 'Inter' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Helvetica, sans-serif', label: 'Helvetica' },
  { value: '"Roboto Mono", monospace', label: 'Roboto Mono' },
  { value: '"Courier New", monospace', label: 'Courier New' },
  { value: 'Georgia, serif', label: 'Georgia' }
];

const FONT_WEIGHTS = [
  { value: 'normal', label: 'Normal' },
  { value: '500', label: 'Medium' },
  { value: '600', label: 'Semibold' },
  { value: 'bold', label: 'Bold' }
];

export default function TextBoxConfigModal({
  element,
  availableTags = [],
  onSave,
  onClose,
  darkMode = false
}) {
  const defaultBgColor = darkMode ? '#1e293b' : '#ffffff';
  const isTransparent = element?.backgroundColor === 'transparent';
  const [transparentBg, setTransparentBg] = useState(isTransparent);
  const [displayTrend, setDisplayTrend] = useState(element?.displayTrend || false);
  const [config, setConfig] = useState({
    textContent: element?.textContent || 'Label',
    tagName: element?.tagName || '',
    showTagValue: element?.showTagValue || false,
    valueFormat: element?.valueFormat || '{value}',
    decimalPlaces: element?.decimalPlaces ?? '',
    thousandsSep: element?.thousandsSep || false,
    prefix: element?.prefix || '',
    suffix: element?.suffix || '',
    fontSize: element?.fontSize || 14,
    fontFamily: element?.fontFamily || 'Inter, system-ui, sans-serif',
    fontWeight: element?.fontWeight || 'normal',
    textAlign: element?.textAlign || 'center',
    color: element?.color || (darkMode ? '#e5e7eb' : '#111827'),
    backgroundColor: isTransparent ? defaultBgColor : (element?.backgroundColor || (darkMode ? '#1e293b' : '#ffffff')),
    borderColor: element?.borderColor || (darkMode ? '#475569' : '#d1d5db'),
    borderWidth: element?.borderWidth ?? 1,
    borderRadius: element?.borderRadius || 4,
    width: element?.width || 120,
    height: element?.height || 40
  });

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave({
      ...element,
      ...config,
      backgroundColor: transparentBg ? 'transparent' : config.backgroundColor,
      displayTrend,
      type: 'textbox'
    });
  };

  const bgColor = darkMode ? '#1e293b' : '#ffffff';
  const textColor = darkMode ? '#e5e7eb' : '#111827';
  const borderColor = darkMode ? '#334155' : '#e5e7eb';
  const inputBg = darkMode ? '#0f172a' : '#f8fafc';

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: `1px solid ${borderColor}`,
    backgroundColor: inputBg,
    color: textColor,
    fontSize: '14px',
    outline: 'none'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: '500',
    color: darkMode ? '#94a3b8' : '#64748b',
    marginBottom: '4px'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        backgroundColor: bgColor,
        borderRadius: '12px',
        width: '480px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: `1px solid ${borderColor}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Type size={20} color={darkMode ? '#60a5fa' : '#3b82f6'} />
            <h3 style={{ margin: 0, color: textColor, fontSize: '16px', fontWeight: '600' }}>
              Configure Text Box
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex'
            }}
          >
            <X size={20} color={darkMode ? '#94a3b8' : '#64748b'} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Text Content */}
          <div>
            <label style={labelStyle}>Text Content</label>
            <input
              type="text"
              value={config.textContent}
              onChange={(e) => handleChange('textContent', e.target.value)}
              placeholder="Enter text..."
              style={inputStyle}
            />
          </div>

          {/* Tag Binding */}
          <div style={{
            padding: '16px',
            backgroundColor: darkMode ? '#0f172a' : '#f1f5f9',
            borderRadius: '8px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Tag size={16} color={darkMode ? '#60a5fa' : '#3b82f6'} />
              <span style={{ fontSize: '14px', fontWeight: '500', color: textColor }}>
                Tag Binding
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <input
                type="checkbox"
                checked={config.showTagValue}
                onChange={(e) => handleChange('showTagValue', e.target.checked)}
                style={{ width: '16px', height: '16px' }}
              />
              <span style={{ fontSize: '13px', color: textColor }}>Show tag value instead of text</span>
            </div>

            {config.showTagValue && (
              <>
                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>Select Tag</label>
                  <SearchableTagSelect
                    value={config.tagName}
                    onChange={(val) => {
                      setConfig(prev => ({ ...prev, tagName: val, textContent: val || prev.textContent }));
                    }}
                    tags={availableTags}
                    placeholder="-- Select a tag --"
                    darkMode={darkMode}
                  />
                </div>

                {/* Number format options */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={labelStyle}>Decimal Places</label>
                    <input
                      type="number"
                      value={config.decimalPlaces}
                      onChange={(e) => handleChange('decimalPlaces', e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value) || 0))}
                      placeholder="Auto"
                      min={0} max={10}
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '20px' }}>
                    <input
                      type="checkbox"
                      id="thousandsSep"
                      checked={config.thousandsSep}
                      onChange={(e) => handleChange('thousandsSep', e.target.checked)}
                      style={{ width: '15px', height: '15px' }}
                    />
                    <label htmlFor="thousandsSep" style={{ fontSize: '13px', color: textColor, cursor: 'pointer' }}>
                      1,000 separator
                    </label>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={labelStyle}>Prefix</label>
                    <input
                      type="text"
                      value={config.prefix}
                      onChange={(e) => handleChange('prefix', e.target.value)}
                      placeholder="e.g. $"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Suffix / Unit</label>
                    <input
                      type="text"
                      value={config.suffix}
                      onChange={(e) => handleChange('suffix', e.target.value)}
                      placeholder="e.g. °C, kg, %"
                      style={inputStyle}
                    />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Value Format Template</label>
                  <input
                    type="text"
                    value={config.valueFormat}
                    onChange={(e) => handleChange('valueFormat', e.target.value)}
                    placeholder="{value}"
                    style={inputStyle}
                  />
                  <p style={{ fontSize: '11px', color: darkMode ? '#64748b' : '#94a3b8', marginTop: '4px' }}>
                    Use {'{value}'} for tag value, {'{label}'} for label, {'{tag}'} for tag name
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Font Settings */}
          <div>
            <label style={labelStyle}>Font Settings</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ ...labelStyle, fontSize: '11px' }}>Font Family</label>
                <select
                  value={config.fontFamily}
                  onChange={(e) => handleChange('fontFamily', e.target.value)}
                  style={inputStyle}
                >
                  {FONT_FAMILIES.map(font => (
                    <option key={font.value} value={font.value}>{font.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: '11px' }}>Font Weight</label>
                <select
                  value={config.fontWeight}
                  onChange={(e) => handleChange('fontWeight', e.target.value)}
                  style={inputStyle}
                >
                  {FONT_WEIGHTS.map(weight => (
                    <option key={weight.value} value={weight.value}>{weight.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Size and Alignment */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelStyle}>Font Size (px)</label>
              <input
                type="number"
                value={config.fontSize}
                onChange={(e) => handleChange('fontSize', parseInt(e.target.value) || 14)}
                min={8}
                max={72}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Text Align</label>
              <div style={{ display: 'flex', gap: '4px' }}>
                {[
                  { value: 'left', icon: AlignLeft },
                  { value: 'center', icon: AlignCenter },
                  { value: 'right', icon: AlignRight }
                ].map(align => {
                  const Icon = align.icon;
                  const isActive = config.textAlign === align.value;
                  return (
                    <button
                      key={align.value}
                      onClick={() => handleChange('textAlign', align.value)}
                      style={{
                        flex: 1,
                        padding: '8px',
                        border: `1px solid ${isActive ? (darkMode ? '#3b82f6' : '#2563eb') : borderColor}`,
                        borderRadius: '6px',
                        backgroundColor: isActive ? (darkMode ? '#1e3a5f' : '#dbeafe') : inputBg,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Icon size={16} color={isActive ? (darkMode ? '#60a5fa' : '#2563eb') : textColor} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Colors */}
          <div>
            <label style={labelStyle}>Colors</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ ...labelStyle, fontSize: '11px' }}>Text</label>
                <input
                  type="color"
                  value={config.color}
                  onChange={(e) => handleChange('color', e.target.value)}
                  style={{ ...inputStyle, padding: '4px', height: '36px' }}
                />
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: '11px' }}>Background</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <input
                    type="checkbox"
                    id="transparentBg"
                    checked={transparentBg}
                    onChange={(e) => setTransparentBg(e.target.checked)}
                    style={{ width: '14px', height: '14px' }}
                  />
                  <label htmlFor="transparentBg" style={{ ...labelStyle, marginBottom: 0, fontSize: '11px', cursor: 'pointer' }}>
                    Transparent
                  </label>
                </div>
                <input
                  type="color"
                  value={config.backgroundColor || '#ffffff'}
                  onChange={(e) => handleChange('backgroundColor', e.target.value)}
                  disabled={transparentBg}
                  style={{ ...inputStyle, padding: '4px', height: '36px', opacity: transparentBg ? 0.4 : 1 }}
                />
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: '11px' }}>Border</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <input
                    type="checkbox"
                    id="borderlessCb"
                    checked={config.borderWidth === 0}
                    onChange={(e) => handleChange('borderWidth', e.target.checked ? 0 : 1)}
                    style={{ width: '14px', height: '14px' }}
                  />
                  <label htmlFor="borderlessCb" style={{ ...labelStyle, marginBottom: 0, fontSize: '11px', cursor: 'pointer' }}>
                    Borderless
                  </label>
                </div>
                <input
                  type="color"
                  value={config.borderColor}
                  onChange={(e) => handleChange('borderColor', e.target.value)}
                  disabled={config.borderWidth === 0}
                  style={{ ...inputStyle, padding: '4px', height: '36px', opacity: config.borderWidth === 0 ? 0.4 : 1 }}
                />
              </div>
            </div>
          </div>

          {/* Border Settings */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelStyle}>Border Width (px)</label>
              <input
                type="number"
                value={config.borderWidth}
                onChange={(e) => handleChange('borderWidth', parseInt(e.target.value) || 0)}
                min={0}
                max={10}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Border Radius (px)</label>
              <input
                type="number"
                value={config.borderRadius}
                onChange={(e) => handleChange('borderRadius', parseInt(e.target.value) || 0)}
                min={0}
                max={50}
                style={inputStyle}
              />
            </div>
          </div>

          {/* Dimensions */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelStyle}>Width (px)</label>
              <input
                type="number"
                value={config.width}
                onChange={(e) => handleChange('width', parseInt(e.target.value) || 100)}
                min={40}
                max={500}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Height (px)</label>
              <input
                type="number"
                value={config.height}
                onChange={(e) => handleChange('height', parseInt(e.target.value) || 40)}
                min={20}
                max={200}
                style={inputStyle}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          padding: '16px 20px',
          borderTop: `1px solid ${borderColor}`
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={displayTrend}
              onChange={(e) => setDisplayTrend(e.target.checked)}
              style={{ width: '15px', height: '15px' }}
            />
            <span style={{ fontSize: '13px', fontWeight: '500', color: textColor }}>Display Trend on Click</span>
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: `1px solid ${borderColor}`,
              backgroundColor: 'transparent',
              color: textColor,
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: darkMode ? '#3b82f6' : '#2563eb',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Save Changes
          </button>
          </div>
        </div>
      </div>
    </div>
  );
}
